import AsyncStorage from '@react-native-async-storage/async-storage';
import { ScopedStorageKeyBuilder } from './ScopedStorageKeyBuilder';

/**
 * ScopedAsyncStorage
 * Wrapper around AsyncStorage that auto-scopes all operations
 * by userId and deviceId.
 */
export class ScopedAsyncStorage {
  private readonly userId: string | null;
  private readonly deviceId: string;

  /**
   * Create a new ScopedAsyncStorage instance
   * @param userId - User ID (null for guest users)
   * @param deviceId - Device ID
   */
  constructor(userId: string | null, deviceId: string) {
    this.userId = userId;
    this.deviceId = deviceId;
  }

  /**
   * Get user ID for this scope
   */
  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Get device ID for this scope
   */
  getDeviceId(): string {
    return this.deviceId;
  }

  /**
   * Build a scoped key from a base key
   */
  private buildKey(baseKey: string): string {
    return ScopedStorageKeyBuilder.buildKey(this.userId, this.deviceId, baseKey);
  }

  /**
   * Get item from storage
   * @param key - Base key (will be automatically scoped)
   */
  async getItem(key: string): Promise<string | null> {
    const scopedKey = this.buildKey(key);
    return AsyncStorage.getItem(scopedKey);
  }

  /**
   * Set item in storage
   * @param key - Base key (will be automatically scoped)
   * @param value - Value to store
   */
  async setItem(key: string, value: string): Promise<void> {
    const scopedKey = this.buildKey(key);
    return AsyncStorage.setItem(scopedKey, value);
  }

  /**
   * Remove item from storage
   * @param key - Base key (will be automatically scoped)
   */
  async removeItem(key: string): Promise<void> {
    const scopedKey = this.buildKey(key);
    return AsyncStorage.removeItem(scopedKey);
  }

  /**
   * Get multiple items from storage
   * @param keys - Array of base keys (will be automatically scoped)
   * @returns Array of [baseKey, value] pairs
   */
  async multiGet(keys: string[]): Promise<readonly [string, string | null][]> {
    const scopedKeys = keys.map(key => this.buildKey(key));
    const results = await AsyncStorage.multiGet(scopedKeys);
    
    // Map back to base keys
    return results.map(([scopedKey, value], index) => [keys[index], value] as const);
  }

  /**
   * Set multiple items in storage
   * @param keyValuePairs - Array of [baseKey, value] pairs
   */
  async multiSet(keyValuePairs: readonly [string, string][]): Promise<void> {
    const scopedPairs = keyValuePairs.map(
      ([key, value]) => [this.buildKey(key), value] as [string, string]
    );
    return AsyncStorage.multiSet(scopedPairs);
  }

  /**
   * Remove multiple items from storage
   * @param keys - Array of base keys (will be automatically scoped)
   */
  async multiRemove(keys: string[]): Promise<void> {
    const scopedKeys = keys.map(key => this.buildKey(key));
    return AsyncStorage.multiRemove(scopedKeys);
  }

  /**
   * Get all keys within the current scope
   * @returns Array of base keys (scope prefix stripped)
   */
  async getAllKeysInScope(): Promise<string[]> {
    const allKeys = await AsyncStorage.getAllKeys();
    const scopePrefix = ScopedStorageKeyBuilder.getScopePrefix(this.userId, this.deviceId);
    
    return allKeys
      .filter(key => key.startsWith(scopePrefix))
      .map(key => key.slice(scopePrefix.length));
  }

  /**
   * Clear all data within the current scope
   */
  async clearScope(): Promise<void> {
    const keysInScope = await this.getAllKeysInScope();
    if (keysInScope.length > 0) {
      await this.multiRemove(keysInScope);
    }
  }

  /**
   * Merge value into existing JSON object
   * @param key - Base key (will be automatically scoped)
   * @param value - JSON string to merge
   */
  async mergeItem(key: string, value: string): Promise<void> {
    const scopedKey = this.buildKey(key);
    return AsyncStorage.mergeItem(scopedKey, value);
  }

  /**
   * Check if a key exists in storage within the current scope
   * @param key - Base key to check
   */
  async hasKey(key: string): Promise<boolean> {
    const value = await this.getItem(key);
    return value !== null;
  }
}
